import React, { useContext, useEffect, useMemo, useState, useRef, useImperativeHandle, forwardRef, useCallback } from "react";
import { JPBReferenceProperty, YearGrouping, DEFAULT_ASSUMPTIONS, JPBAssumptions, AreaUnit, PriceMode, SoilQualityGroup, SOIL_QUALITY_GROUPS, AverageCalculationMethod, JPBPriceIndexSource } from "./JPBState";
import { ViewButtonIcon, ViewButtonDropdown } from "src/propertyInfoTemplates/PropertyComponents";
import { AppMessagesContext, Localization, SettingsManager, actionSetErrorMessage, actionSetInfoMessage } from "@viamap/viamap2-common";
import ReactDataGrid, { renderSortIcon, renderSortPriority } from "react-data-grid";
import { autoUpdate, flip, offset, useDismiss, useFloating, useHover, useInteractions, shift } from "@floating-ui/react";
import { floatingBoundaryMiddleware, MITFloatingPortal, useFloatingPortalContext } from "src/componentsUtils/PortalHelpers";
import { getCheckedItemsFromCurrentAssumptions, formatCurrency, formatPricePerSquareMeter, formatArea, formatDate, getComparator, formatPercentage, formatIndexDifferenceValue, calculateEffectiveMarkSquareMeterPrice, calculateSoilSpecificAgriculturalPrices, calculatePropertyEstimatedPrices, mapSoilTypeToGroup, getResolvedJPBAssumptions, getSoilQualityFactorsByGroup, getNonAgricultureToAgricultureRatio } from "./JPBFunc";
import { openWindow, WindowId } from "src/WindowManager/WindowState";
import { useWindowContext } from "src/WindowManager/useWindowContext";
import { GlassCheckbox } from "src/components/MitGlassComponents";
import { DataCollector } from "src/dataCollector/DataCollector";
import { JPBManager } from "./JPBManager";
import { ExportData } from "src/managers/ExportData";
import { SheetFunc } from "src/managers/SheetFunc";
import { ApplicationStateContext, Feature } from "src/states/ApplicationState";
import { ProtectedFeature } from "src/components/ProtectedFeature";
import { MIBIcon } from "src/componentsUtils/MIBIcon";
import { JPBTableSortColumn, useJPBSessionSlice } from "./JPBSessionState";
import { actionAddDataLayer, MapitStateContext } from "src/states/MapitState";
import { ActionTracker } from "src/common/managers/ActionTracker";
import { createJPBReferencePropertiesDataLayer } from "./JPBExportToDataLayer";

const getFirstBfeNrFromString = (value: string | undefined | null): number | null => {
   if (!value) return null;
   const match = value.match(/\d+/);
   if (!match) return null;
   const n = parseInt(match[0], 10);
   return Number.isFinite(n) ? n : null;
};

const getJPBAdkomstWindowParams = (row: JPBReferenceProperty) => {
   const dokumentId = row.documentID || row.dokumentId;
   const bfeNumber = getFirstBfeNrFromString(row.bfe_nr) ?? undefined;
   return {
      adkomst: {
         dokumentId,
         tinglysningsDato: row.transactionDate instanceof Date
            ? row.transactionDate.toISOString()
            : row.transactionDate,
      },
      bfeNumber,
   };
};

/** Column keys removed from the JPB table UI and column picker (cannot be re-enabled). */
const JPB_TABLE_REMOVED_COLUMN_KEYS = new Set([
   "transactionStatus",
   "reasonText",
   /** Exclusion reason — shown on map hover only */
   "reasonCode",
   "matchedBfeNr",
   "markOverlapLayerYear",
   "markOverlapCalculatedAt",
   "jordbundskortCalculatedAt",
   "soilJB_7_10"
]);

function HeaderLabel(props: { label: React.ReactNode; tooltip?: string }) {
   return (
      <div
         style={{
            display: "flex",
            alignItems: "center",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            width: "100%",
            height: "100%"
         }}
      >
         <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{props.label}</span>
      </div>
   );
}

function HeaderWithSort(props: {
   label: React.ReactNode;
   tooltip?: string;
   sortDirection?: 'ASC' | 'DESC';
   priority?: number;
   sortable?: boolean;
}) {
   const { root } = useFloatingPortalContext();
   const [open, setOpen] = useState(false);
   const { refs, floatingStyles, context } = useFloating({
      whileElementsMounted: autoUpdate,
      open,
      onOpenChange: setOpen,
      middleware: [
         offset(6),
         ...(root != null ? floatingBoundaryMiddleware(root) : [flip({ padding: 8 }), shift({ padding: 8 })])
      ],
      placement: "top-start",
   });

   const { getReferenceProps, getFloatingProps } = useInteractions([
      useHover(context, { move: false, delay: { open: 250, close: 80 } }),
      useDismiss(context),
   ]);

   return (
      <>
         <span style={{ display: "flex", alignItems: "center", width: "100%", minWidth: 0, overflow: "hidden", paddingInlineEnd: 12 }}>
            <span style={{ flex: "1 1 0", overflow: "hidden", minWidth: 0 }}>
               <div ref={refs.setReference} {...getReferenceProps()}>
                  <div
                     style={{
                        display: "flex",
                        alignItems: "center",
                        width: "100%",
                        height: "100%",
                        cursor: props.tooltip ? "help" : "default"
                     }}
                  >
                     <HeaderLabel label={props.label} />
                  </div>
               </div>
            </span>
            {props.sortable && (
               <span style={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
                  {renderSortIcon({ sortDirection: props.sortDirection })}
                  {renderSortPriority({ priority: props.priority })}
               </span>
            )}
         </span>
         {Boolean(props.tooltip && open) && (
            <MITFloatingPortal>
               <div
                  ref={refs.setFloating}
                  className="ViewButtonToolTip assumptions-box"
                  style={{
                     ...floatingStyles,
                     maxWidth: "360px",
                     whiteSpace: "pre-line",
                     lineHeight: 1.35
                  }}
                  {...getFloatingProps()}
               >
                  {props.tooltip}
               </div>
            </MITFloatingPortal>
         )}
      </>
   );
}

function getJpbTableHorizontalScrollElement(wrapper: HTMLElement | null): HTMLElement | null {
   if (!wrapper) return null;
   const outer = wrapper.closest(".jpb-table-grid") as HTMLElement | null;
   const rdg = wrapper.querySelector(".rdg") as HTMLElement | null;
   const viewport = wrapper.querySelector(".rdg-viewport") as HTMLElement | null;
   const candidates = [viewport, rdg, wrapper, outer].filter((el): el is HTMLElement => el != null);
   let best: HTMLElement | null = null;
   let bestOverflow = -1;
   for (const el of candidates) {
      const overflow = el.scrollWidth - el.clientWidth;
      if (overflow > bestOverflow) {
         best = el;
         bestOverflow = overflow;
      }
   }
   return best;
}

function JPBTableTopHorizontalScroll(props: {
   wrapperRef: React.RefObject<HTMLDivElement | null>;
   syncKey: string;
}) {
   const topRef = useRef<HTMLDivElement>(null);
   const spacerRef = useRef<HTMLDivElement>(null);
   const syncingRef = useRef(false);
   const [needed, setNeeded] = useState(false);

   useEffect(() => {
      const wrapper = props.wrapperRef.current;
      const top = topRef.current;
      const spacer = spacerRef.current;
      if (!wrapper || !top || !spacer) return;

      let attached: HTMLElement | null = null;

      const onGridScroll = () => {
         if (syncingRef.current || !attached) return;
         syncingRef.current = true;
         top.scrollLeft = attached.scrollLeft;
         syncingRef.current = false;
      };

      const attachGrid = () => {
         const scrollEl = getJpbTableHorizontalScrollElement(wrapper);
         if (scrollEl === attached) return;
         if (attached) attached.removeEventListener("scroll", onGridScroll);
         attached = scrollEl;
         if (attached) attached.addEventListener("scroll", onGridScroll, { passive: true });
      };

      const update = () => {
         attachGrid();
         if (!attached) return;
         spacer.style.width = `${Math.max(attached.scrollWidth, attached.clientWidth)}px`;
         setNeeded(attached.scrollWidth > attached.clientWidth + 1);
         if (!syncingRef.current) {
            top.scrollLeft = attached.scrollLeft;
         }
      };

      const onTopScroll = () => {
         if (syncingRef.current || !attached) return;
         syncingRef.current = true;
         attached.scrollLeft = top.scrollLeft;
         syncingRef.current = false;
      };

      top.addEventListener("scroll", onTopScroll, { passive: true });
      update();

      const resizeObserver = new ResizeObserver(() => update());
      resizeObserver.observe(wrapper);
      const rdg = wrapper.querySelector(".rdg");
      if (rdg) resizeObserver.observe(rdg);
      const outer = wrapper.closest(".jpb-table-grid");
      if (outer && outer !== wrapper) resizeObserver.observe(outer);

      const mutationObserver = new MutationObserver(() => update());
      mutationObserver.observe(wrapper, { childList: true, subtree: true, attributes: true });

      return () => {
         top.removeEventListener("scroll", onTopScroll);
         if (attached) attached.removeEventListener("scroll", onGridScroll);
         resizeObserver.disconnect();
         mutationObserver.disconnect();
      };
   }, [props.syncKey, props.wrapperRef]);

   return (
      <div
         ref={topRef}
         className={"jpb-table-hscroll" + (needed ? "" : " jpb-table-hscroll--idle")}
         title={Localization.getText("jpb:tableHorizontalScroll")}
         aria-hidden={!needed}
      >
         <div ref={spacerRef} className="jpb-table-hscroll-spacer" />
      </div>
   );
}

function AddressCell(props: {
   row: JPBReferenceProperty;
   addressByBfe: Record<number, string>;
   mergeAddressCache: (updates: Record<number, string>) => void;
   inFlightBfesRef: React.MutableRefObject<Set<number>>;
}) {
   const { row, addressByBfe, mergeAddressCache, inFlightBfesRef } = props;

   useEffect(() => {
      let _cancelled = false;

      async function fetchAddress() {
         const backendAddress = (row.address ?? "").trim();
         if (backendAddress && backendAddress !== "-") return;

         const bfe = getFirstBfeNrFromString(row.bfe_nr);
         if (!bfe) return;

         if (addressByBfe[bfe]) return;
         if (inFlightBfesRef.current.has(bfe)) return;
         inFlightBfesRef.current.add(bfe);

         try {
            const address = (await DataCollector.DF_GQL.EBR.EBRAdresseOgPunkt([bfe.toString()]))[0]?.betegnelse
            if (address) {
               mergeAddressCache({ [bfe]: address });
            }
         } catch {
            // ignore
         } finally {
            inFlightBfesRef.current.delete(bfe);
         }
      }

      fetchAddress();

      return () => {
         _cancelled = true;
      };
   }, [row.bfe_nr, row.address, addressByBfe, mergeAddressCache, inFlightBfesRef]);

   const display = (row.address ?? "").trim();
   return <div>{display && display !== "-" ? display : "-"}</div>;
}

type JPBTableProps = {
   data: JPBReferenceProperty[];
   checkedItems: Set<string>;
   onCheckedItemsChange: (checkedItems: Set<string>) => void;
   onRowClick: (property: JPBReferenceProperty) => void;
   yearGrouping: YearGrouping;
   averageCalculationMethod: AverageCalculationMethod;
   calculationDateTo?: Date;
   /** Latest `dato` in the whole JPB index (from {@link JPBManager.fetchDateBounds}); used when `calculationDateTo` is unset. */
   indexMaxTransactionDate?: Date;
   hideUnselected: 'none' | 'unchecked' | 'locked';
   onHideUnselectedChange: (mode: 'none' | 'unchecked' | 'locked') => void;
   customAssumptions?: Partial<JPBAssumptions>;
   onCustomAssumptionsChange?: (assumptions: Partial<JPBAssumptions>) => void;
   areaUnit: AreaUnit;
   priceMode: PriceMode;
   priceIndexSource: JPBPriceIndexSource;
}

export type JPBTableRef = {
   scrollToRow: (documentID: string) => void;
}

export const JPBTable = forwardRef<JPBTableRef, JPBTableProps>((props, ref) => {
   const gridRef = useRef<any>(null);
   const [highlightedRow, setHighlightedRow] = useState<string | null>(null);
   const highlightTimeoutRef = useRef<NodeJS.Timeout | null>(null);
   const { dispatch: windowDispatch } = useWindowContext();
   const { dispatch: mapitDispatch } = useContext(MapitStateContext);
   const { dispatch: appMessageDispatch } = useContext(AppMessagesContext);
   const { hasAccessToFeature, state: applicationState } = useContext(ApplicationStateContext);
   const { root: floatingPortalRoot } = useFloatingPortalContext();
   const accessRightsResolved = applicationState.availableFeatures.length > 0;
   const canShowErrorColumn = hasAccessToFeature(Feature.Debugging);
   const hiddenColumnKeys = useMemo(() => {
      if (!accessRightsResolved || canShowErrorColumn) return JPB_TABLE_REMOVED_COLUMN_KEYS;
      const next = new Set(JPB_TABLE_REMOVED_COLUMN_KEYS);
      next.add("error");
      return next;
   }, [accessRightsResolved, canShowErrorColumn]);
   const [tableUiState, setTableUiState] = useJPBSessionSlice("tableUi");

   const addressByBfe = tableUiState.addressByBfe;
   const addressByBfeRef = useRef<Record<number, string>>({});
   const inFlightBfesRef = useRef<Set<number>>(new Set());
   const visibleColumns = useMemo(() => new Set(tableUiState.visibleColumns), [tableUiState.visibleColumns]);
   const sortColumns = tableUiState.sortColumns;

   const mergeAddressCache = (updates: Record<number, string>) => {
      setTableUiState((prev) => {
         const merged = { ...prev.addressByBfe, ...updates };
         addressByBfeRef.current = merged;
         return {
            ...prev,
            addressByBfe: merged
         };
      });
   };

   useEffect(() => {
      addressByBfeRef.current = addressByBfe;
   }, [addressByBfe]);

   const setVisibleColumnsState = useCallback((value: Set<string> | ((prev: Set<string>) => Set<string>)) => {
      setTableUiState((prev) => {
         const prevSet = new Set(prev.visibleColumns);
         const nextSet = typeof value === "function" ? value(prevSet) : value;
         return {
            ...prev,
            visibleColumns: Array.from(nextSet)
         };
      });
   }, [setTableUiState]);

   const setSortColumnsState = useCallback((value: readonly JPBTableSortColumn[] | ((prev: readonly JPBTableSortColumn[]) => readonly JPBTableSortColumn[])) => {
      setTableUiState((prev) => {
         const next = typeof value === "function" ? value(prev.sortColumns) : value;
         return {
            ...prev,
            sortColumns: [...next]
         };
      });
   }, [setTableUiState]);

   useEffect(() => {
      setVisibleColumnsState((prev) => {
         let changed = false;
         const next = new Set(prev);
         hiddenColumnKeys.forEach((k) => {
            if (next.delete(k)) changed = true;
         });
         if (prev.has("soilJB_7_10")) {
            next.add("soilJB_7_9");
            next.add("soilJB_10");
            changed = true;
         }
         return changed ? next : prev;
      });
      setSortColumnsState((prev) => {
         let changed = false;
         const next = prev.flatMap((sort) => {
            if (sort.columnKey === "soilJB_7_10") {
               changed = true;
               return [{ ...sort, columnKey: "soilJB_7_9" }];
            }
            if (sort.columnKey === "error" && hiddenColumnKeys.has("error")) {
               changed = true;
               return [];
            }
            return [sort];
         });
         return changed ? next : prev;
      });
   }, [hiddenColumnKeys, setVisibleColumnsState, setSortColumnsState]);

   useEffect(() => {
      setVisibleColumnsState(prev => {
         const next = new Set(prev);
         if (props.priceMode === PriceMode.Landbrug) {
            next.add('markOverlap');
         } else {
            next.delete('markOverlap');
         }
         return next;
      });
   }, [props.priceMode, setVisibleColumnsState]);

   const currentAssumptions: JPBAssumptions = getResolvedJPBAssumptions(props.customAssumptions);
   const nonAgriToAgriRatio = getNonAgricultureToAgricultureRatio(currentAssumptions);
   const isIndexApplied = props.priceIndexSource !== JPBPriceIndexSource.None;
   const indexedLandPriceLabel = isIndexApplied ? Localization.getText("Jordpris (indekseret)") : Localization.getText("Jordpris");
   const indexedUnitPriceLabel = props.areaUnit === AreaUnit.Hectare
      ? Localization.getText(isIndexApplied ? "Price per ha (indexed)" : "Price per ha")
      : Localization.getText(isIndexApplied ? "Price per m² (indexed)" : "Price per m²");
   const lockedDocumentIds = useMemo(() => {
      const locked = new Set<string>();
      for (const prop of props.data) {
         if (JPBManager.getSelectionLockState(prop, props.customAssumptions).isLocked) {
            locked.add(prop.documentID);
         }
      }
      return locked;
   }, [props.data, props.customAssumptions, currentAssumptions.buildingAreaOkM2, currentAssumptions.buildingAreaOkUseAgriculturalArea]);

   // Calculate soil-specific prices and estimated prices for each property
   const propertiesWithEstimates = useMemo(() => {
      const checkedData = props.data.filter(d => props.checkedItems.has(d.documentID));
      if (checkedData.length === 0) {
         return props.data.map(prop => ({ ...prop, estimatedPricePerSquareMeter: null }));
      }

      const soilFactorsByGroup = getSoilQualityFactorsByGroup(currentAssumptions);
      const soilPrices = calculateSoilSpecificAgriculturalPrices(
         checkedData,
         nonAgriToAgriRatio,
         soilFactorsByGroup,
         props.averageCalculationMethod
      );
      if (!soilPrices) {
         return props.data.map(prop => ({ ...prop, estimatedPricePerSquareMeter: null }));
      }

      return calculatePropertyEstimatedPrices(props.data, soilPrices, nonAgriToAgriRatio);
   }, [props.data, props.checkedItems, currentAssumptions, nonAgriToAgriRatio, props.averageCalculationMethod]);

   // Helper function to calculate soil group percentages for a property
   const getSoilGroupPercentages = (prop: JPBReferenceProperty): { [group: string]: number } => {
      const percentages: { [group: string]: number } = {};
      const jordbundskortOverlap = prop.overlapArea?.jordbundskortOverlap;

      if (!jordbundskortOverlap) {
         return percentages;
      }

      // Sum up percentages for each soil quality group
      for (const [soilType, percentage] of Object.entries(jordbundskortOverlap)) {
         if (typeof percentage !== 'number' || percentage <= 0) {
            continue;
         }

         const soilGroup = mapSoilTypeToGroup(soilType);
         if (!soilGroup) {
            continue;
         }

         // Accumulate percentages for each soil group
         if (percentages[soilGroup]) {
            percentages[soilGroup] += percentage;
         } else {
            percentages[soilGroup] = percentage;
         }
      }

      return percentages;
   };

   const assumptionsContent = useMemo(() => {
      const systemAssumptions = SettingsManager.getSystemSetting("jpb.assumptions", DEFAULT_ASSUMPTIONS) as typeof DEFAULT_ASSUMPTIONS;
      const hasCustomPrice = (
         (props.customAssumptions?.pricePerMeterMin !== undefined && props.customAssumptions.pricePerMeterMin !== systemAssumptions.pricePerMeterMin) ||
         (props.customAssumptions?.pricePerMeterMax !== undefined && props.customAssumptions.pricePerMeterMax !== systemAssumptions.pricePerMeterMax)
      );
      const hasCustomArea = (
         (props.customAssumptions?.areaMin !== undefined && props.customAssumptions.areaMin !== systemAssumptions.areaMin) ||
         (props.customAssumptions?.areaMax !== undefined && props.customAssumptions.areaMax !== systemAssumptions.areaMax)
      );
      const hasCustomMarkOverlap = (
         props.customAssumptions?.effectiveMarkOverlapThreshold !== undefined &&
         props.customAssumptions.effectiveMarkOverlapThreshold !== systemAssumptions.effectiveMarkOverlapThreshold
      );

      const toDate = JPBManager.resolveDisplayCalculationToDate({
         calculationDateTo: props.calculationDateTo,
         indexMaxTransactionDate: props.indexMaxTransactionDate,
         loadedTransactions: props.data
      });

      const fromDate = new Date(toDate);
      fromDate.setFullYear(fromDate.getFullYear() - props.yearGrouping);
      const formatInteger = new Intl.NumberFormat(Localization.getLocale(), {
         maximumFractionDigits: 0
      });
      const formatHa = new Intl.NumberFormat(Localization.getLocale(), {
         minimumFractionDigits: 0,
         maximumFractionDigits: 2
      });
      const areaMinM2 = Math.max(0, currentAssumptions.areaMin ?? 0);
      const areaMinHa = areaMinM2 / 10000;
      const areaMaxM2 = Math.max(0, currentAssumptions.areaMax ?? 0);
      const areaMaxHa = areaMaxM2 / 10000;
      const buildingMinM2 = Math.max(0, currentAssumptions.buildingAreaOkM2 ?? 0);
      const buildingMinHa = buildingMinM2 / 10000;
      const formatTransactionType = (value: string) => value
         .replace(/([A-Z])/g, " $1")
         .replace(/^./, str => str.toUpperCase())
         .replace(/\s+/g, " ")
         .trim();

      return (
         <div style={{ flex: 1 }}>
            <div style={{ fontWeight: "bold", marginBottom: "8px" }}>
               {Localization.getText("Current assumptions:")}
            </div>
            <ul style={{
               paddingLeft: "24px",
               listStyleType: "disc",
               margin: 0
            }}>
               <li style={{ marginBottom: "4px" }}>
                  {props.areaUnit === AreaUnit.Hectare ? Localization.getText("Price per ha") : Localization.getText("Price per m²")}
                  <span>: <span style={{ fontStyle: "italic", color: hasCustomPrice ? "#3a8e87" : "inherit" }}>
                     {formatPricePerSquareMeter(currentAssumptions.pricePerMeterMin, props.areaUnit, 0)} - {formatPricePerSquareMeter(currentAssumptions.pricePerMeterMax, props.areaUnit, 0)}
                     {hasCustomPrice && <span style={{ fontSize: "0.85em", marginLeft: "4px" }}>({Localization.getText("customized")})</span>}
                  </span></span>
               </li>
               <li style={{ marginBottom: "4px" }}>
                  {Localization.getText("Transaction interval")}
                  <span>: <span style={{ fontStyle: "italic", color: "#3a8e87" }}>
                     {formatDate(fromDate)} - {formatDate(toDate)}
                  </span></span>
               </li>
               <li style={{ marginBottom: "4px" }}>
                  {Localization.getText("Area interval")}
                  <span>: <span style={{ fontStyle: "italic", color: hasCustomArea ? "#3a8e87" : "inherit" }}>
                     {formatHa.format(areaMinHa)} - {formatHa.format(areaMaxHa)} ha ({formatInteger.format(areaMinM2)} - {formatInteger.format(areaMaxM2)} m²)
                     {hasCustomArea && <span style={{ fontSize: "0.85em", marginLeft: "4px" }}>({Localization.getText("customized")})</span>}
                  </span></span>
               </li>
               <li style={{ marginBottom: "4px" }}>
                  {Localization.getText("Farmland overlap threshold")}
                  <span>: <span style={{ fontStyle: "italic", color: hasCustomMarkOverlap ? "#3a8e87" : "inherit" }}>
                     &gt;{currentAssumptions.effectiveMarkOverlapThreshold}%
                     {hasCustomMarkOverlap && <span style={{ fontSize: "0.85em", marginLeft: "4px" }}>({Localization.getText("customized")})</span>}
                  </span></span>
               </li>
               <li style={{ marginBottom: "4px" }}>
                  {Localization.getText("Included properties with building")}
                  <span>: <span style={{ fontStyle: "italic" }}>
                     {currentAssumptions.excludeBuildings ? (
                        Localization.getText("None")
                     ) : (
                        <>{formatInteger.format(buildingMinM2)} m² ({formatHa.format(buildingMinHa)} ha)</>
                     )}
                  </span></span>
               </li>
               <li>
                  {Localization.getText("Included transaction types")}
                  <span>: <span style={{ fontStyle: "italic" }}>{currentAssumptions.includeTransactionTypes.map(formatTransactionType).join(", ")}</span></span>
               </li>
            </ul>
         </div>
      );
   }, [currentAssumptions, props.customAssumptions, props.areaUnit, props.yearGrouping, props.data, props.calculationDateTo, props.indexMaxTransactionDate]);

   // Tooltip state for info icon
   const [infoTooltipOpen, setInfoTooltipOpen] = useState(false);
   const { refs: infoRefs, floatingStyles: infoFloatingStyles, context: infoTooltipContext } = useFloating({
      whileElementsMounted: autoUpdate,
      open: infoTooltipOpen,
      middleware: [
         offset(5),
         ...(floatingPortalRoot != null
            ? floatingBoundaryMiddleware(floatingPortalRoot)
            : [flip({ padding: 8 }), shift({ padding: 8 })])
      ],
      onOpenChange: setInfoTooltipOpen,
   });
   const {
      getFloatingProps: getInfoTooltipFloatingProps
   } = useInteractions([useHover(infoTooltipContext), useDismiss(infoTooltipContext)]);

   const [columnVisibilityOpen, setColumnVisibilityOpen] = useState(false);
   const { refs: columnVisibilityRefs, floatingStyles: columnVisibilityFloatingStyles, context: columnVisibilityContext } = useFloating({
      whileElementsMounted: autoUpdate,
      open: columnVisibilityOpen,
      middleware: [
         offset(5),
         ...(floatingPortalRoot != null
            ? floatingBoundaryMiddleware(floatingPortalRoot)
            : [flip({ padding: 8 }), shift({ padding: 8 })])
      ],
      onOpenChange: setColumnVisibilityOpen,
   });
   const {
      getFloatingProps: getColumnVisibilityFloatingProps
   } = useInteractions([useDismiss(columnVisibilityContext)]);

   const handleColumnVisibilityChange = (columnKey: string, visible: boolean) => {
      if (hiddenColumnKeys.has(columnKey)) return;
      const newVisibleColumns = new Set(visibleColumns);
      if (visible) {
         newVisibleColumns.add(columnKey);
      } else {
         newVisibleColumns.delete(columnKey);
      }
      setVisibleColumnsState(newVisibleColumns);
   };

   const soilColumnToGroup: Record<string, SoilQualityGroup> = Object.fromEntries(
      SOIL_QUALITY_GROUPS.map((group) => [`soil${group.replace(/[\s-]+/g, '_')}`, group])
   );

   const compareNullableNumberKeepMissingLast = (
      aValue: number | null | undefined,
      bValue: number | null | undefined,
      direction: 'ASC' | 'DESC'
   ) => {
      const aMissing = aValue === null || aValue === undefined || !Number.isFinite(aValue);
      const bMissing = bValue === null || bValue === undefined || !Number.isFinite(bValue);
      if (aMissing && bMissing) return 0;
      if (aMissing) return 1;
      if (bMissing) return -1;
      return direction === 'ASC' ? aValue - bValue : bValue - aValue;
   };

   const compareNullableStringKeepMissingLast = (
      aValue: string | null | undefined,
      bValue: string | null | undefined,
      direction: 'ASC' | 'DESC'
   ) => {
      const aText = aValue ?? "";
      const bText = bValue ?? "";
      const aMissing = !aText;
      const bMissing = !bText;
      if (aMissing && bMissing) return 0;
      if (aMissing) return 1;
      if (bMissing) return -1;
      return direction === 'ASC'
         ? aText.localeCompare(bText, Localization.getLocale())
         : bText.localeCompare(aText, Localization.getLocale());
   };

   const getSelectionIssueText = (row: JPBReferenceProperty): string | null => {
      return JPBManager.getTransactionIssueText(row.transactionStatus, row.reasonText, row.reasonCode);
   };
   const getLandbrugsnoteringDisplay = (row: JPBReferenceProperty): string => row.landbrugsnotering?.trim() || "-";

   const handleSortColumnsChange = (newSortColumns: readonly JPBTableSortColumn[]) => {
      setSortColumnsState(newSortColumns);
   };

   const getHeaderTooltip = (columnKey: string): string | undefined => {
      const areaUnitLabel = props.areaUnit === AreaUnit.Hectare ? "ha" : "m²";
      const sourceLabel = JPBManager.getPriceIndexSourceLabel(props.priceIndexSource);
      const replaceUnit = (text: string) => text.replace(/{unit}/g, areaUnitLabel);
      const formatFactor = (value: number) => new Intl.NumberFormat(Localization.getLocale(), {
         minimumFractionDigits: 0,
         maximumFractionDigits: 2,
         useGrouping: false
      }).format(value);
      switch (columnKey) {
         case "transactionDate":
            return Localization.getText("tooltip:transactionDate");
         case "area":
            return replaceUnit(Localization.getText("tooltip:area"));
         case "originalTotalPrice":
            return Localization.getText("tooltip:originalTotalPrice");
         case "totalPrice":
            if (!isIndexApplied) {
               return Localization.getText("tooltip:totalPriceNoIndex");
            }
            return Localization.getFormattedText("tooltip:totalPrice", { source: sourceLabel });
         case "indexDifferencePct":
            return Localization.getText("tooltip:indexDifferencePct");
         case "pricePerSquareMeter":
            if (props.priceMode === PriceMode.Landbrug) {
               if (!isIndexApplied) {
                  return Localization.getFormattedText("tooltip:pricePerSquareMeterLandbrugNoIndex", {
                     unit: areaUnitLabel,
                     agricultureFactor: formatFactor(currentAssumptions.agricultureFactor),
                     nonAgricultureFactor: formatFactor(currentAssumptions.nonAgricultureFactor),
                     ratioPct: formatFactor(nonAgriToAgriRatio * 100)
                  });
               }
               return Localization.getFormattedText("tooltip:pricePerSquareMeterLandbrug", {
                  unit: areaUnitLabel,
                  source: sourceLabel,
                  agricultureFactor: formatFactor(currentAssumptions.agricultureFactor),
                  nonAgricultureFactor: formatFactor(currentAssumptions.nonAgricultureFactor),
                  ratioPct: formatFactor(nonAgriToAgriRatio * 100)
               });
            }
            if (!isIndexApplied) {
               return replaceUnit(Localization.getText("tooltip:pricePerSquareMeterNoIndex"));
            }
            return replaceUnit(Localization.getFormattedText("tooltip:pricePerSquareMeter", { source: sourceLabel }));
         case "markOverlap":
            return Localization.getText("tooltip:markOverlap");
         case "prisDriftsbygning":
            return Localization.getText("tooltip:prisDriftsbygning");
         case "prisStuehus":
            return Localization.getText("tooltip:prisStuehus");
         case "estimatedPricePerSquareMeter":
            return Localization.getText("tooltip:estimatedPricePerSquareMeter");
         case "transactionType":
            return Localization.getText("tooltip:transactionType");
         case "selectionIssue":
            return Localization.getText("tooltip:selectionIssue");
         case "bfe_nr":
            return Localization.getText("tooltip:bfe_nr");
         case "address":
            return Localization.getText("tooltip:address");
         case "landbrugsnotering":
            return "Agricultural notation carried over from the source data.";
         case "documentID":
            return Localization.getText("tooltip:documentID");
         default:
            if (columnKey.startsWith("soil")) {
               return Localization.getText("tooltip:soilShare");
            }
            return undefined;
      }
   };

   const sortedData = useMemo(() => {
      let dataToSort = props.data;
      if (props.hideUnselected === 'unchecked') {
         dataToSort = props.data.filter(row => props.checkedItems.has(row.documentID));
      } else if (props.hideUnselected === 'locked') {
         dataToSort = props.data.filter(row => !lockedDocumentIds.has(row.documentID));
      }

      if (sortColumns.length === 0) return [...dataToSort];

      const sorted = [...dataToSort].sort((a, b) => {
         for (const sort of sortColumns) {
            let comparator;
            let comparatorAlreadyDirected = false;
            // Use custom comparator for pricePerSquareMeter based on priceMode
            if (sort.columnKey === 'pricePerSquareMeter' && props.priceMode === PriceMode.Landbrug) {
               comparator = (a: JPBReferenceProperty, b: JPBReferenceProperty) => {
                  const aValue = calculateEffectiveMarkSquareMeterPrice(a, nonAgriToAgriRatio);
                  const bValue = calculateEffectiveMarkSquareMeterPrice(b, nonAgriToAgriRatio);
                  if (aValue === null && bValue === null) return 0;
                  if (aValue === null) return 1;
                  if (bValue === null) return -1;
                  return aValue - bValue;
               };
            } else if (sort.columnKey === 'estimatedPricePerSquareMeter') {
               comparator = (a: JPBReferenceProperty, b: JPBReferenceProperty) => {
                  const aProp = propertiesWithEstimates.find(p => p.documentID === a.documentID);
                  const bProp = propertiesWithEstimates.find(p => p.documentID === b.documentID);
                  const aValue = aProp?.estimatedPricePerSquareMeter ?? null;
                  const bValue = bProp?.estimatedPricePerSquareMeter ?? null;
                  if (aValue === null && bValue === null) return 0;
                  if (aValue === null) return 1;
                  if (bValue === null) return -1;
                  return aValue - bValue;
               };
            } else if (sort.columnKey === 'indexDifferencePct') {
               comparatorAlreadyDirected = true;
               comparator = (a: JPBReferenceProperty, b: JPBReferenceProperty) => {
                  return compareNullableNumberKeepMissingLast(a.indexDifferencePct, b.indexDifferencePct, sort.direction);
               };
            } else if (sort.columnKey in soilColumnToGroup) {
               const groupKey = soilColumnToGroup[sort.columnKey];
               comparatorAlreadyDirected = true;
               comparator = (a: JPBReferenceProperty, b: JPBReferenceProperty) => {
                  const aPercentages = getSoilGroupPercentages(a);
                  const bPercentages = getSoilGroupPercentages(b);
                  return compareNullableNumberKeepMissingLast(aPercentages[groupKey], bPercentages[groupKey], sort.direction);
               };
            } else if (sort.columnKey === 'prisDriftsbygning') {
               comparatorAlreadyDirected = true;
               comparator = (a: JPBReferenceProperty, b: JPBReferenceProperty) => {
                  return compareNullableNumberKeepMissingLast(a.prisDriftsbygning, b.prisDriftsbygning, sort.direction);
               };
            } else if (sort.columnKey === 'prisStuehus') {
               comparatorAlreadyDirected = true;
               comparator = (a: JPBReferenceProperty, b: JPBReferenceProperty) => {
                  return compareNullableNumberKeepMissingLast(a.prisStuehus, b.prisStuehus, sort.direction);
               };
            } else if (sort.columnKey === 'selectionIssue') {
               comparatorAlreadyDirected = true;
               comparator = (a: JPBReferenceProperty, b: JPBReferenceProperty) => {
                  return compareNullableStringKeepMissingLast(
                     getSelectionIssueText(a),
                     getSelectionIssueText(b),
                     sort.direction
                  );
               };
            } else {
               comparator = getComparator(sort.columnKey);
            }
            const compResult = comparator(a, b);
            if (compResult !== 0) {
               return comparatorAlreadyDirected ? compResult : (sort.direction === 'ASC' ? compResult : -compResult);
            }
         }
         return 0;
      });

      return sorted;
   }, [props.data, sortColumns, props.checkedItems, props.hideUnselected, props.priceMode, currentAssumptions, propertiesWithEstimates, lockedDocumentIds]);

   const displayRows = useMemo(() => {
      // Only create new row objects for rows where we resolved an address and the row doesn't already have one.
      if (Object.keys(addressByBfe).length === 0) return sortedData;

      return sortedData.map((row) => {
         const backendAddress = (row.address ?? "").trim();
         if (backendAddress && backendAddress !== "-") return row;

         const bfe = getFirstBfeNrFromString(row.bfe_nr);
         const resolved = bfe ? addressByBfe[bfe] : undefined;
         if (!resolved) return row;

         return { ...row, address: resolved };
      });
   }, [sortedData, addressByBfe]);

   const selectableRows = useMemo(() => {
      return sortedData.filter(row => !lockedDocumentIds.has(row.documentID));
   }, [sortedData, lockedDocumentIds]);

   const selectedRows = useMemo(() => {
      return new Set(sortedData.filter(row => props.checkedItems.has(row.documentID)).map(row => row.documentID));
   }, [sortedData, props.checkedItems]);

   // Expose scrollToRow method via ref
   useImperativeHandle(ref, () => ({
      scrollToRow: (documentID: string) => {
         const rowIndex = sortedData.findIndex(row => row.documentID === documentID);

         if (rowIndex === -1) {
            console.warn(`Row with documentID ${documentID} not found in sortedData`);
            return;
         }

         if (!gridRef.current) {
            console.warn('gridRef.current is null');
            return;
         }


         requestAnimationFrame(() => {
            const gridApi = gridRef.current as any;
            if (!gridApi) {
               console.warn('gridApi is null');
               return;
            }

            const gridElement = gridApi.element;
            if (!gridElement) {
               console.warn('gridElement is null');
               return;
            }

            const scrollContainer = gridElement.querySelector('.rdg-viewport') ||
               gridElement.querySelector('[role="grid"]') ||
               gridElement;

            if (!scrollContainer) {
               console.warn('Could not find scroll container');
               return;
            }

            const rowHeight = 35;
            const containerHeight = scrollContainer.clientHeight;
            const targetScrollTop = (rowIndex * rowHeight) - (containerHeight / 4) + (rowHeight / 4);

            const maxScrollTop = Math.max(0, scrollContainer.scrollHeight - containerHeight);
            const finalScrollTop = Math.max(0, Math.min(targetScrollTop, maxScrollTop));

            scrollContainer.scrollTo({
               top: finalScrollTop,
               behavior: 'smooth'
            });

            if (highlightTimeoutRef.current) {
               clearTimeout(highlightTimeoutRef.current);
            }

            setHighlightedRow(documentID);
            highlightTimeoutRef.current = setTimeout(() => {
               setHighlightedRow(null);
               highlightTimeoutRef.current = null;
            }, 5000);
         });
      }
   }), [sortedData, selectedRows, props.onCheckedItemsChange]);

   const handleSelectedRowsChange = (newSelectedRows: Set<string>) => {
      if (lockedDocumentIds.size === 0) {
         props.onCheckedItemsChange(newSelectedRows);
         return;
      }
      const filtered = new Set<string>();
      newSelectedRows.forEach((id) => {
         if (!lockedDocumentIds.has(id)) filtered.add(id);
      });
      props.onCheckedItemsChange(filtered);
   };

   // Handle reset assumptions
   const handleResetAssumptions = () => {
      const { checkedItems: defaultCheckedItems } = getCheckedItemsFromCurrentAssumptions(
         props.data,
         props.customAssumptions,
         props.priceMode
      );
      props.onCheckedItemsChange(defaultCheckedItems);
   };

   const handleExcludeBuildingsToggle = () => {
      const currentExcludeBuildings = props.customAssumptions?.excludeBuildings ?? false;
      const newAssumptions = {
         ...props.customAssumptions,
         excludeBuildings: !currentExcludeBuildings
      };
      props.onCustomAssumptionsChange?.(newAssumptions);
      const { checkedItems: newCheckedItems } = getCheckedItemsFromCurrentAssumptions(
         props.data,
         newAssumptions,
         props.priceMode
      );
      props.onCheckedItemsChange(newCheckedItems);
   };

   const exportCheckedToExcel = () => {
      if (props.data.length === 0) return;

      const dataColumns = allColumns.filter(col =>
         col.key !== "select-row" && col.key !== "documentIDAction" && col.key !== "error"
      );
      const headers = dataColumns.map(col => col.name);

      const getRecordFromRow = (row: JPBReferenceProperty) => {
         const record: Record<string, string> = {};
         for (const col of dataColumns) {
            const key = col.key;
            let val: string;
            if (key === "transactionDate") val = formatDate(row.transactionDate);
            else if (key === "originalTotalPrice") val = formatCurrency(row.originalTotalPrice);
            else if (key === "totalPrice") val = formatCurrency(row.totalPrice);
            else if (key === "indexDifferencePct") val = formatIndexDifferenceValue(row.indexDifferencePct);
            else if (key === "area") val = formatArea(row.area, props.areaUnit);
            else if (key === "pricePerSquareMeter") {
               const price = props.priceMode === PriceMode.Landbrug
                  ? calculateEffectiveMarkSquareMeterPrice(row, nonAgriToAgriRatio)
                  : row.pricePerSquareMeter;
               val = price !== null && price !== undefined ? formatPricePerSquareMeter(price, props.areaUnit) : "-";
            } else if (key === "markOverlap") val = formatPercentage(row.overlapArea?.markOverlap);
            else if (key === "documentID") val = row.documentID;
            else if (key === "bfe_nr") val = row.bfe_nr ?? "";
            else if (key === "address") val = (row.address ?? "").trim() && (row.address ?? "").trim() !== "-" ? (row.address ?? "").trim() : "-";
            else if (key === "transactionType") {
               const value = row.transactionType || "";
               val = value.replace(/([A-Z])/g, " $1").replace(/^./, str => str.toUpperCase()).replace(/\s+/g, " ").trim();
            } else if (key === "selectionIssue") {
               val = getSelectionIssueText(row) ?? "-";
            } else if (key === "landbrugsnotering") {
               val = getLandbrugsnoteringDisplay(row);
            } else if (key === "estimatedPricePerSquareMeter") {
               const propWithEstimate = propertiesWithEstimates.find(p => p.documentID === row.documentID);
               const estimatedPrice = propWithEstimate?.estimatedPricePerSquareMeter;
               val = estimatedPrice !== null && estimatedPrice !== undefined ? formatPricePerSquareMeter(estimatedPrice, props.areaUnit) : "-";
            } else if (key in soilColumnToGroup) {
               const percentages = getSoilGroupPercentages(row);
               const groupKey = soilColumnToGroup[key];
               const percentage = percentages[groupKey] ?? 0;
               val = percentage > 0 ? formatPercentage(percentage) : "-";
            } else if (key === "prisDriftsbygning") val = row.prisDriftsbygning != null ? formatCurrency(row.prisDriftsbygning) : "-";
            else if (key === "prisStuehus") val = row.prisStuehus != null ? formatCurrency(row.prisStuehus) : "-";
            else val = "";
            record[col.name] = val;
         }
         return record;
      };

      const allRowsForExport = props.data.map((row) => {
         const backendAddress = (row.address ?? "").trim();
         if (backendAddress && backendAddress !== "-") return row;
         const bfe = getFirstBfeNrFromString(row.bfe_nr);
         const resolved = bfe ? addressByBfeRef.current[bfe] : undefined;
         if (!resolved) return row;
         return { ...row, address: resolved };
      });

      const selectedColumnName = "Valgt";
      const allRowsDump = allRowsForExport.map((row) => {
         const selectedStatus = props.checkedItems.has(row.documentID)
            ? "Valgt"
            : lockedDocumentIds.has(row.documentID)
               ? "Ikke relevant"
               : "Ikke valgt";
         return {
            [selectedColumnName]: selectedStatus,
            ...getRecordFromRow(row)
         };
      });

      const wb = SheetFunc.createWorkBookFromJSON(
         { Title: "JPB Properties", Author: "Mapit", CreatedDate: new Date() },
         [{ sheetName: "All properties", headers: [selectedColumnName, ...headers], rows: allRowsDump }]
      );
      const blob = SheetFunc.createBlob(wb);
      ExportData.downloadBlob(`jpb-checked-properties-${Date.now()}.xlsx`, blob);
   };

   const exportCheckedToDataLayer = () => {
      const checkedRows = props.data.filter((row) => props.checkedItems.has(row.documentID));
      if (checkedRows.length === 0) {
         appMessageDispatch(
            actionSetErrorMessage(Localization.getText("jpb:exportToDataLayerNoSelection"))
         );
         return;
      }

      const estimatedPriceByDocumentId = new Map(
         propertiesWithEstimates.map((prop) => [
            prop.documentID,
            prop.estimatedPricePerSquareMeter ?? null,
         ])
      );

      try {
         const layer = createJPBReferencePropertiesDataLayer({
            properties: checkedRows,
            priceMode: props.priceMode,
            nonAgriToAgriRatio,
            estimatedPriceByDocumentId,
         });
         if (!layer) {
            appMessageDispatch(
               actionSetErrorMessage(Localization.getText("jpb:exportToDataLayerFailed"))
            );
            return;
         }

         mapitDispatch(actionAddDataLayer(layer, true, true));
         appMessageDispatch(
            actionSetInfoMessage(
               Localization.getFormattedText("jpb:exportToDataLayerAdded", {
                  count: String(layer.geoJson?.features?.length ?? checkedRows.length),
               })
            )
         );
         void ActionTracker.track("JPBModule", "Action", "ExportToDataLayer", {
            count: layer.geoJson?.features?.length ?? checkedRows.length,
         });
      } catch (error: any) {
         appMessageDispatch(
            actionSetErrorMessage(error?.message || String(error))
         );
      }
   };

   const createRenderCell = (content: (row: JPBReferenceProperty) => React.ReactNode) => {
      return ({ row }: { row: JPBReferenceProperty }) => <div>{content(row)}</div>;
   };

   // Define all columns (including those that might be hidden)
   const allColumns = useMemo(() => [
      {
         key: 'select-row',
         name: '',
         width: 35,
         minWidth: 35,
         maxWidth: 35,
         resizable: false,
         sortable: false,
         renderCell: ({ row }: { row: JPBReferenceProperty }) => {
            const isLocked = lockedDocumentIds.has(row.documentID);
            if (isLocked) {
               return <div style={{ width: '100%', height: '100%' }} />;
            }
            const isSelected = selectedRows.has(row.documentID);
            const handleCheckboxChange = () => {
               const newSelected = new Set(props.checkedItems);
               if (!isSelected) {
                  newSelected.add(row.documentID);
               } else {
                  newSelected.delete(row.documentID);
               }
               handleSelectedRowsChange(newSelected);
            };
            return (
               <div
                  className="rdg-checkbox-label"
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%', cursor: 'pointer' }}
                  onClick={(e) => {
                     e.stopPropagation();
                     handleCheckboxChange();
                  }}
                  onMouseDown={(e) => {
                     e.stopPropagation();
                  }}
               >
                  <input
                     className="rdg-checkbox-input"
                     type="checkbox"
                     checked={isSelected}
                     readOnly
                     onClick={(e) => {
                        e.stopPropagation();
                        handleCheckboxChange();
                     }}
                     onMouseDown={(e) => {
                        e.stopPropagation();
                     }}
                     style={{ pointerEvents: 'auto', cursor: 'pointer' }}
                  />
                  <span
                     className="rdg-checkbox-icon"
                     style={isSelected ? {
                        background: '#3a8e87',
                        borderColor: '#3a8e87'
                     } : {
                        borderColor: '#3a8e87'
                     }}
                  />
               </div>
            );
         },
         renderHeaderCell: () => {
            const allSelectableRowsSelected = selectableRows.length > 0 && selectableRows.every(row => selectedRows.has(row.documentID));
            const handleHeaderCheckboxChange = () => {
               if (!allSelectableRowsSelected) {
                  const allSelectable = new Set(selectableRows.map(row => row.documentID));
                  handleSelectedRowsChange(allSelectable);
               } else {
                  handleSelectedRowsChange(new Set());
               }
            };
            return (
               <div
                  className="rdg-checkbox-label"
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%', cursor: 'pointer' }}
                  onClick={(e) => {
                     e.stopPropagation();
                     handleHeaderCheckboxChange();
                  }}
                  onMouseDown={(e) => {
                     e.stopPropagation();
                  }}
               >
                  <input
                     className="rdg-checkbox-input"
                     type="checkbox"
                     checked={allSelectableRowsSelected}
                     readOnly
                     onClick={(e) => {
                        e.stopPropagation();
                        handleHeaderCheckboxChange();
                     }}
                     onMouseDown={(e) => {
                        e.stopPropagation();
                     }}
                     style={{ pointerEvents: 'auto', cursor: 'pointer' }}
                  />
                  <span
                     className="rdg-checkbox-icon"
                     style={allSelectableRowsSelected ? {
                        background: '#3a8e87',
                        borderColor: '#3a8e87'
                     } : {
                        borderColor: '#3a8e87'
                     }}
                  />
               </div>
            );
         }
      },
      {
         key: 'documentIDAction',
         name: "",
         width: 18,
         cellClass: "jpb-action-cell",
         headerClass: "jpb-action-header",
         summaryCellClass: "jpb-action-summary",
         sortable: false,
         resizable: false,
         visible: visibleColumns.has('documentIDAction'),
         renderCell: (cellData) => {
            const dokumentId = cellData.row.documentID || cellData.row.dokumentId;
            return (
               <ViewButtonIcon
                  title={Localization.getText("View adkomst details and bilag")}
                  disabled={!dokumentId}
                  onClick={(e) => {
                     e.preventDefault();
                     e.stopPropagation();
                     if (!dokumentId) return;
                     windowDispatch(openWindow(WindowId.AdkomstDetails, getJPBAdkomstWindowParams(cellData.row)));
                  }}
               ><MIBIcon icon={"skøde"} /></ViewButtonIcon>
            );
         },
         renderSummaryCell: () => <div></div>
      },
      {
         key: 'transactionDate',
         name: Localization.getText("Handelsdato"),
         minWidth: 130,
         width: 'minmax(130px, 1fr)',
         sortable: true,
         resizable: true,
         visible: visibleColumns.has('transactionDate'),
         renderHeaderCell: ({ column, sortDirection, priority }) => (
            <HeaderWithSort
               label={Localization.getText("Handelsdato")}
               tooltip={getHeaderTooltip("transactionDate")}
               sortDirection={sortDirection}
               priority={priority}
               sortable={column.sortable}
            />
         ),
         renderCell: createRenderCell((row) => formatDate(row.transactionDate)),
         renderSummaryCell: () => <div>-</div>
      },
      {
         key: 'originalTotalPrice',
         name: Localization.getText("Handelspris"),
         minWidth: 170,
         width: 'minmax(170px, 1fr)',
         sortable: true,
         resizable: true,
         visible: visibleColumns.has('originalTotalPrice'),
         renderHeaderCell: ({ column, sortDirection, priority }) => (
            <HeaderWithSort
               label={Localization.getText("Handelspris")}
               tooltip={getHeaderTooltip("originalTotalPrice")}
               sortDirection={sortDirection}
               priority={priority}
               sortable={column.sortable}
            />
         ),
         renderCell: createRenderCell((row) => (
            <div style={{ textAlign: "right" }}>{formatCurrency(row.originalTotalPrice)}</div>
         )),
         renderSummaryCell: () => <div>-</div>
      },
      {
         key: 'totalPrice',
         name: indexedLandPriceLabel,
         minWidth: 170,
         width: 'minmax(170px, 1fr)',
         sortable: true,
         resizable: true,
         visible: visibleColumns.has('totalPrice'),
         renderHeaderCell: ({ column, sortDirection, priority }) => (
            <HeaderWithSort
               label={indexedLandPriceLabel}
               tooltip={getHeaderTooltip("totalPrice")}
               sortDirection={sortDirection}
               priority={priority}
               sortable={column.sortable}
            />
         ),
         renderCell: createRenderCell((row) => (
            <div style={{ textAlign: "right" }}>{formatCurrency(row.totalPrice)}</div>
         )),
         renderSummaryCell: () => <div>-</div>
      },
      {
         key: 'indexDifferencePct',
         name: Localization.getText("Index difference"),
         minWidth: 150,
         width: 'minmax(150px, 1fr)',
         sortable: true,
         resizable: true,
         visible: visibleColumns.has('indexDifferencePct'),
         renderHeaderCell: ({ column, sortDirection, priority }) => (
            <HeaderWithSort
               label={Localization.getText("Index difference")}
               tooltip={getHeaderTooltip("indexDifferencePct")}
               sortDirection={sortDirection}
               priority={priority}
               sortable={column.sortable}
            />
         ),
         renderCell: createRenderCell((row) => (
            <div style={{ textAlign: "right" }}>{formatIndexDifferenceValue(row.indexDifferencePct)}</div>
         )),
         renderSummaryCell: () => <div>-</div>
      },
      {
         key: 'area',
         name: Localization.getText("M_Area") + (props.areaUnit === AreaUnit.Hectare ? " (ha)" : " (m²)"),
         minWidth: 120,
         width: 'minmax(120px, 1fr)',
         sortable: true,
         resizable: true,
         visible: visibleColumns.has('area'),
         renderHeaderCell: ({ column, sortDirection, priority }) => (
            <HeaderWithSort
               label={Localization.getText("M_Area") + (props.areaUnit === AreaUnit.Hectare ? " (ha)" : " (m²)")}
               tooltip={getHeaderTooltip("area")}
               sortDirection={sortDirection}
               priority={priority}
               sortable={column.sortable}
            />
         ),
         renderCell: createRenderCell((row) => (
            <div style={{ textAlign: "right" }}>{formatArea(row.area, props.areaUnit)}</div>
         )),
         renderSummaryCell: () => <div>-</div>
      },
      {
         key: 'pricePerSquareMeter',
         name: indexedUnitPriceLabel,
         minWidth: 150,
         width: 'minmax(150px, 1fr)',
         sortable: true,
         resizable: true,
         cellClass: "jpb-price-cell",
         headerCellClass: "jpb-price-header",
         summaryCellClass: "jpb-price-summary",
         visible: visibleColumns.has('pricePerSquareMeter'),
         renderHeaderCell: ({ column, sortDirection, priority }) => (
            <HeaderWithSort
               label={indexedUnitPriceLabel}
               tooltip={getHeaderTooltip("pricePerSquareMeter")}
               sortDirection={sortDirection}
               priority={priority}
               sortable={column.sortable}
            />
         ),
         renderCell: createRenderCell((row) => {
            const price = props.priceMode === PriceMode.Landbrug
               ? calculateEffectiveMarkSquareMeterPrice(row, nonAgriToAgriRatio)
               : row.pricePerSquareMeter;
            return (
               <div style={{ textAlign: "right" }}>{price !== null && price !== undefined ? formatPricePerSquareMeter(price, props.areaUnit) : "-"}</div>
            );
         }),
         renderSummaryCell: () => <div>-</div>
      },
      {
         key: 'markOverlap',
         name: Localization.getText("Farmland overlap"),
         minWidth: 130,
         width: 'minmax(130px, 1fr)',
         sortable: true,
         resizable: true,
         visible: visibleColumns.has('markOverlap'),
         renderHeaderCell: ({ column, sortDirection, priority }) => (
            <HeaderWithSort
               label={Localization.getText("Farmland overlap")}
               tooltip={getHeaderTooltip("markOverlap")}
               sortDirection={sortDirection}
               priority={priority}
               sortable={column.sortable}
            />
         ),
         renderCell: createRenderCell((row) => (
            <div style={{ textAlign: "right" }}>{formatPercentage(row.overlapArea?.markOverlap)}</div>
         )),
         renderSummaryCell: () => <div>-</div>
      },
      {
         key: 'documentID',
         name: Localization.getText("Document ID"),
         minWidth: 9,
         width: 'minmax(9px, 1fr)',
         resizable: true,
         sortable: true,
         visible: visibleColumns.has('documentID'),
         renderHeaderCell: ({ column, sortDirection, priority }) => (
            <HeaderWithSort
               label={Localization.getText("Document ID")}
               tooltip={getHeaderTooltip("documentID")}
               sortDirection={sortDirection}
               priority={priority}
               sortable={column.sortable}
            />
         ),
         renderCell: createRenderCell((row) => row.documentID),
         renderSummaryCell: ({ row }) => <div>{row.documentID}</div>
      },
      {
         key: 'bfe_nr',
         name: Localization.getText("BFE No."),
         minWidth: 120,
         width: 'minmax(120px, 1fr)',
         sortable: true,
         resizable: true,
         visible: visibleColumns.has('bfe_nr'),
         renderHeaderCell: ({ column, sortDirection, priority }) => (
            <HeaderWithSort
               label={Localization.getText("BFE No.")}
               tooltip={getHeaderTooltip("bfe_nr")}
               sortDirection={sortDirection}
               priority={priority}
               sortable={column.sortable}
            />
         ),
         renderCell: createRenderCell((row) => row.bfe_nr),
         renderSummaryCell: ({ row }) => <div>{row.bfe_nr}</div>
      },
      {
         key: 'address',
         name: Localization.getText("Address"),
         minWidth: 120,
         width: 'minmax(120px, 1fr)',
         sortable: true,
         resizable: true,
         visible: visibleColumns.has('address'),
         renderHeaderCell: ({ column, sortDirection, priority }) => (
            <HeaderWithSort
               label={Localization.getText("Address")}
               tooltip={getHeaderTooltip("address")}
               sortDirection={sortDirection}
               priority={priority}
               sortable={column.sortable}
            />
         ),
         renderCell: ({ row }: { row: JPBReferenceProperty }) => (
            <AddressCell
               row={row}
               addressByBfe={addressByBfe}
               mergeAddressCache={mergeAddressCache}
               inFlightBfesRef={inFlightBfesRef}
            />
         ),
         renderSummaryCell: () => <div>-</div>
      },
      {
         key: 'transactionType',
         name: Localization.getText("Transaction type"),
         minWidth: 170,
         width: 'minmax(170px, 1fr)',
         sortable: true,
         resizable: true,
         visible: visibleColumns.has('transactionType'),
         renderHeaderCell: ({ column, sortDirection, priority }) => (
            <HeaderWithSort
               label={Localization.getText("Transaction type")}
               tooltip={getHeaderTooltip("transactionType")}
               sortDirection={sortDirection}
               priority={priority}
               sortable={column.sortable}
            />
         ),
         renderCell: createRenderCell((row) => {
            const value = row.transactionType || "";
            const titleCased = value
               .replace(/([A-Z])/g, ' $1')
               .replace(/^./, str => str.toUpperCase())
               .replace(/\s+/g, ' ')
               .trim();
            return titleCased;
         }),
         renderSummaryCell: () => <div>-</div>
      },
      {
         key: 'landbrugsnotering',
         name: 'Landbrugsnotering',
         minWidth: 170,
         width: 'minmax(170px, 1fr)',
         sortable: true,
         resizable: true,
         visible: visibleColumns.has('landbrugsnotering'),
         renderHeaderCell: ({ column, sortDirection, priority }) => (
            <HeaderWithSort
               label="Landbrugsnotering"
               tooltip={getHeaderTooltip("landbrugsnotering")}
               sortDirection={sortDirection}
               priority={priority}
               sortable={column.sortable}
            />
         ),
         renderCell: createRenderCell((row) => getLandbrugsnoteringDisplay(row)),
         renderSummaryCell: () => <div>-</div>
      },
      {
         key: 'estimatedPricePerSquareMeter',
         name: props.areaUnit === AreaUnit.Hectare ? Localization.getText("Estimated price per ha") : Localization.getText("Estimated price per m²"),
         minWidth: 150,
         width: 'minmax(150px, 1fr)',
         sortable: true,
         resizable: true,
         visible: visibleColumns.has('estimatedPricePerSquareMeter'),
         renderHeaderCell: ({ column, sortDirection, priority }) => (
            <HeaderWithSort
               label={props.areaUnit === AreaUnit.Hectare ? Localization.getText("Estimated price per ha") : Localization.getText("Estimated price per m²")}
               tooltip={getHeaderTooltip("estimatedPricePerSquareMeter")}
               sortDirection={sortDirection}
               priority={priority}
               sortable={column.sortable}
            />
         ),
         renderCell: ({ row }: { row: JPBReferenceProperty }) => {
            const propWithEstimate = propertiesWithEstimates.find(p => p.documentID === row.documentID);
            const estimatedPrice = propWithEstimate?.estimatedPricePerSquareMeter;
            return (
               <div style={{ textAlign: "right" }}>
                  {estimatedPrice !== null && estimatedPrice !== undefined
                     ? formatPricePerSquareMeter(estimatedPrice, props.areaUnit)
                     : "-"}
               </div>
            );
         },
         renderSummaryCell: () => <div>-</div>
      },
      ...(canShowErrorColumn ? [{
         key: 'error',
         name: "Err",
         minWidth: 2,
         visible: visibleColumns.has('error'),
         sortable: true,
         resizable: false,
         renderCell: createRenderCell((row) => <div style={{ textAlign: "right" }}>{Math.round(Number(row.error) * 100) + "%"}</div>)
      }] : []),
      // Soil quality group columns
      ...SOIL_QUALITY_GROUPS.map((group) => {
         const columnKey = `soil${group.replace(/[\s-]+/g, '_')}` as string;
         return {
            key: columnKey,
            name: group,
            minWidth: 100,
            width: 'minmax(100px, 1fr)',
            sortable: true,
            resizable: true,
            visible: visibleColumns.has(columnKey),
            renderHeaderCell: ({ column, sortDirection, priority }) => (
               <HeaderWithSort
                  label={group}
                  tooltip={getHeaderTooltip(columnKey)}
                  sortDirection={sortDirection}
                  priority={priority}
                  sortable={column.sortable}
               />
            ),
            renderCell: createRenderCell((row) => {
               const soilPercentages = getSoilGroupPercentages(row);
               const percentage = soilPercentages[group] ?? 0;
               return (
                  <div style={{ textAlign: "right" }}>
                     {percentage > 0 ? formatPercentage(percentage) : "-"}
                  </div>
               );
            }),
            renderSummaryCell: () => <div>-</div>
         };
      }),
      {
         key: 'prisDriftsbygning',
         name: Localization.getText("Pris driftsbygning"),
         minWidth: 150,
         width: 'minmax(150px, 1fr)',
         sortable: true,
         resizable: true,
         visible: visibleColumns.has('prisDriftsbygning'),
         renderHeaderCell: ({ column, sortDirection, priority }) => (
            <HeaderWithSort
               label={Localization.getText("Pris driftsbygning")}
               tooltip={getHeaderTooltip("prisDriftsbygning")}
               sortDirection={sortDirection}
               priority={priority}
               sortable={column.sortable}
            />
         ),
         renderCell: createRenderCell((row) => (
            <div style={{ textAlign: "right" }}>
               {row.prisDriftsbygning !== null && row.prisDriftsbygning !== undefined
                  ? formatCurrency(row.prisDriftsbygning)
                  : "-"}
            </div>
         )),
         renderSummaryCell: () => <div>-</div>
      },
      {
         key: 'prisStuehus',
         name: Localization.getText("Pris stuehus"),
         minWidth: 150,
         width: 'minmax(150px, 1fr)',
         sortable: true,
         resizable: true,
         visible: visibleColumns.has('prisStuehus'),
         renderHeaderCell: ({ column, sortDirection, priority }) => (
            <HeaderWithSort
               label={Localization.getText("Pris stuehus")}
               tooltip={getHeaderTooltip("prisStuehus")}
               sortDirection={sortDirection}
               priority={priority}
               sortable={column.sortable}
            />
         ),
         renderCell: createRenderCell((row) => (
            <div style={{ textAlign: "right" }}>
               {row.prisStuehus !== null && row.prisStuehus !== undefined
                  ? formatCurrency(row.prisStuehus)
                  : "-"}
            </div>
         )),
         renderSummaryCell: () => <div>-</div>
      }
   ], [props.checkedItems, props.areaUnit, props.priceMode, props.priceIndexSource, Localization.getLanguage(), visibleColumns, propertiesWithEstimates, lockedDocumentIds, selectableRows, selectedRows, handleSelectedRowsChange, nonAgriToAgriRatio, currentAssumptions, canShowErrorColumn]);

   const columns = useMemo(() => {
      return allColumns.filter((col) => {
         if (col.key === 'select-row' || col.key === 'documentIDAction') {
            return true;
         }
         return !("visible" in col) || col.visible !== false;
      });
   }, [allColumns]);

   const gridWrapperRef = useRef<HTMLDivElement>(null);
   const prevColumnKeysRef = useRef<string>("");

   useEffect(() => {
      const columnKeys = columns.map((c) => c.key).join(",");
      if (prevColumnKeysRef.current && prevColumnKeysRef.current !== columnKeys) {
         const el = gridWrapperRef.current;
         if (el) {
            requestAnimationFrame(() => {
               el.style.width = "99.99%";
               requestAnimationFrame(() => {
                  el.style.width = "100%";
               });
            });
         }
      }
      prevColumnKeysRef.current = columnKeys;
   }, [columns]);

   return (
      <div className="jpb-table-root">
         <div className="jpb-card jpb-card--padded jpb-card--relative">
            {/* Button menu split into left, center, and right sections */}
            <div className="jpb-table-toolbar">
               {/* LEFT GROUP: building filter + reset */}
               <div className="jpb-table-toolbar-left">
                  <ViewButtonIcon
                     onClick={handleExcludeBuildingsToggle}
                     title={props.customAssumptions?.excludeBuildings ? Localization.getText("Include properties with buildings") : Localization.getText("Exclude properties with buildings")}
                  >
                     {props.customAssumptions?.excludeBuildings ? <MIBIcon icon={"noBuilding"} /> : <MIBIcon icon={"building"} />}
                  </ViewButtonIcon>
                  <ViewButtonIcon
                     onClick={handleResetAssumptions}
                     title={Localization.getText("Reset to current assumptions")}
                  >
                     <MIBIcon icon={"reset"} />
                  </ViewButtonIcon>
               </div>
               {/* CENTER GROUP: header */}
               <div className="jpb-table-toolbar-center jpb-table-toolbar-center-dynamic">
                  <div>{Localization.getText("Sammenligningshandler")}</div>
               </div>
               {/* RIGHT GROUP: hide/show unselected, show columns, info */}
               <div className="jpb-table-toolbar-right">
                  {props.hideUnselected === 'none' ? (
                     <ViewButtonDropdown
                        options={[
                           { text: Localization.getText("Hide unchecked items"), value: 'unchecked' },
                           { text: Localization.getText("Hide locked items"), value: 'locked' }
                        ]}
                        onSelect={(value) => {
                           props.onHideUnselectedChange(value as 'unchecked' | 'locked');
                        }}
                     >
                        <MIBIcon icon={"show"} />
                     </ViewButtonDropdown>
                  ) : (
                     <ViewButtonIcon
                        onClick={() => props.onHideUnselectedChange('none')}
                        title={Localization.getText("Show all items")}
                     >
                        <MIBIcon icon={"hide"} />
                     </ViewButtonIcon>
                  )}
                  <ProtectedFeature feature={Feature.JPBExcelExport} contentsIfNoAccess={<></>}>
                     <ViewButtonIcon
                        onClick={exportCheckedToExcel}
                        title={Localization.getText("Export to Excel")}
                        disabled={props.data.length === 0}
                     >
                        <MIBIcon icon={"xlsx"} />
                     </ViewButtonIcon>
                  </ProtectedFeature>
                  <ProtectedFeature feature={Feature.JPBExportToDataLayer} contentsIfNoAccess={<></>}>
                     <ViewButtonIcon
                        onClick={exportCheckedToDataLayer}
                        title={Localization.getText("jpb:exportToDataLayer")}
                        disabled={props.checkedItems.size === 0}
                     >
                        <MIBIcon icon={"addToMap"} />
                     </ViewButtonIcon>
                  </ProtectedFeature>
                  <div
                     ref={columnVisibilityRefs.setReference}
                     style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer"
                     }}
                  >
                     <ViewButtonIcon
                        onClick={() => setColumnVisibilityOpen(!columnVisibilityOpen)}
                        title={Localization.getText("Column visibility")}
                     >
                        <MIBIcon icon={"columns"} />
                     </ViewButtonIcon>
                  </div>
                  {columnVisibilityOpen && (
                     <MITFloatingPortal>
                        <div
                           ref={columnVisibilityRefs.setFloating}
                           className="ViewButtonToolTip assumptions-box jpb-table-column-visibility-popover"
                           style={{
                              ...columnVisibilityFloatingStyles,
                              height: "300px",
                              maxHeight: "300px"
                           }}
                           {...getColumnVisibilityFloatingProps()}
                        >
                           <div style={{ fontWeight: "bold", marginBottom: "8px", flexShrink: 0 }}>
                              {Localization.getText("Show columns:")}
                           </div>
                           <div style={{ overflowY: "auto", overflowX: "hidden", height: "90%" }}>
                              {allColumns.filter(col => {
                                 if (hiddenColumnKeys.has(col.key)) return false;
                                 if (col.key === 'select-row' || col.key === 'documentIDAction') return false;
                                 return true;
                              }).map((col) => {
                                 const isVisible = visibleColumns.has(col.key);
                                 return (
                                    <GlassCheckbox
                                       key={col.key}
                                       checked={isVisible}
                                       onClick={(e, checked) => handleColumnVisibilityChange(col.key, checked)}
                                       style={{ marginBottom: "4px" }}
                                    >
                                       {col.name}
                                    </GlassCheckbox>
                                 );
                              })}
                           </div>
                        </div>
                     </MITFloatingPortal>
                  )}
                  <div
                     ref={infoRefs.setReference}
                     className="jpb-table-assumptions-icon"
                     style={{ fontSize: "26px", color: "#266b65" }}
                  >
                     <MIBIcon icon="info" />
                  </div>
                  {Boolean(assumptionsContent && infoTooltipOpen) &&
                     <MITFloatingPortal>
                        <div
                           ref={infoRefs.setFloating}
                           className="ViewButtonToolTip assumptions-box"
                           style={{
                              ...infoFloatingStyles
                           }}
                           {...getInfoTooltipFloatingProps()}
                        >
                           {assumptionsContent}
                        </div>
                     </MITFloatingPortal>
                  }
               </div>
            </div>
            <JPBTableTopHorizontalScroll
               wrapperRef={gridWrapperRef}
               syncKey={columns.map((column) => column.key).join(",")}
            />
            {/* Table area */}
            <div className="jpb-table-grid" style={{ overflowX: 'auto', overflowY: 'auto' }}>
               <div
                  ref={gridWrapperRef}
                  className="jpb-table-grid-inner"
                  style={{ width: '100%', height: '100%', minWidth: 0 }}
               >
                  <ReactDataGrid
                     ref={gridRef}
                     className='rdg-light'
                     columns={columns}
                     rows={displayRows}
                     sortColumns={sortColumns}
                     onSortColumnsChange={handleSortColumnsChange}
                     defaultColumnOptions={{ resizable: true, sortable: true }}
                     rowHeight={35}
                     enableVirtualization={true}
                     style={{ width: '100%', height: '100%' }}
                     rowKeyGetter={(row) => row.documentID}
                     rowClass={(row) => {
                        const classes: string[] = [];
                        if (highlightedRow === row.documentID) {
                           classes.push('rdg-row-highlighted');
                        }
                        return classes.join(' ');
                     }}
                     onCellClick={(args) => {
                        if (args.column.key === 'select-row') {
                           return false;
                        }
                        props.onRowClick(args.row);
                     }}
                  />
               </div>
            </div>
         </div>
      </div>
   );
});
